import { useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, RefreshCw } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, streamGenerate } from "@/lib/api";
import { ModelPicker } from "@/components/ModelPicker";
import { toast } from "sonner";

export default function AIDraftDialog({ open, onOpenChange, taskSlug, field, existing, onApply }) {
  const [prompt, setPrompt] = useState("");
  const [output, setOutput] = useState("");
  const [generatedRef, setGeneratedRef] = useState("");
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [model, setModel] = useState(null);
  const outRef = useRef(null);

  useEffect(() => {
    if (!open || !field) return;
    setOutput("");
    setGeneratedRef("");
    setLoadingPrompt(true);
    api
      .post("/ai/build-prompt", { task_slug: taskSlug, field_key: field.key, existing: existing || null })
      .then(({ data }) => setPrompt(data.prompt))
      .catch(() => toast.error("Could not prepare the prompt. Try again."))
      .finally(() => setLoadingPrompt(false));
  }, [open, field, taskSlug, existing]);

  const generate = async () => {
    setStreaming(true);
    setOutput("");
    let acc = "";
    try {
      await streamGenerate({
        taskSlug,
        fieldKey: field.key,
        prompt,
        model,
        onDelta: (d) => {
          acc += d;
          setOutput(acc);
          if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
        },
      });
      setGeneratedRef(acc);
    } catch (e) {
      toast.error(e.message || "The AI is unavailable right now.");
    } finally {
      setStreaming(false);
    }
  };

  const apply = () => {
    if (!output.trim()) return;
    const edited = output.trim() !== generatedRef.trim();
    onApply(output.trim(), edited);
    onOpenChange(false);
    toast.success("Applied to your draft. Marked as AI assisted. Edit it freely, you're the author.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 overflow-hidden p-0" data-testid="ai-draft-dialog">
        <DialogHeader className="border-b border-brand-line bg-brand-surface/40 px-6 py-4">
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Sparkles size={18} className="text-brand" /> AI Studio
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Drafting: <span className="font-medium text-brand-text">{field?.label}</span>. You edit and decide. This is only a first draft.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-6 py-5">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">
              Your prompt (edit if you like)
            </label>
            {loadingPrompt ? (
              <div className="flex h-24 items-center justify-center rounded-lg border border-brand-line bg-slate-50">
                <Loader2 className="animate-spin text-brand" size={18} />
              </div>
            ) : (
              <textarea
                data-testid="ai-prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                className="w-full resize-none rounded-lg border border-brand-line bg-slate-50 p-3 text-sm leading-relaxed focus:border-brand focus:bg-white focus:outline-none"
              />
            )}
            <button
              data-testid="ai-generate-btn"
              onClick={generate}
              disabled={streaming || loadingPrompt}
              className="mt-2 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover disabled:opacity-50"
            >
              {streaming ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />}
              {output ? "Regenerate" : "Generate a draft"}
            </button>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-slate-400">Model</span>
              <ModelPicker value={model} onChange={setModel} disabled={streaming} />
            </div>
          </div>

          {(output || streaming) && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">
                Draft, edit before you use it
              </label>
              <textarea
                ref={outRef}
                data-testid="ai-output"
                value={output}
                onChange={(e) => setOutput(e.target.value)}
                rows={6}
                className="thin-scroll w-full resize-none rounded-lg border border-brand-line bg-white p-3 text-sm leading-relaxed focus:border-brand focus:outline-none"
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-brand-line px-6 py-4">
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-lg px-4 py-2 text-sm text-slate-500 hover:text-slate-800"
          >
            Cancel
          </button>
          <button
            data-testid="ai-apply-btn"
            onClick={apply}
            disabled={!output.trim() || streaming}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-deep px-5 py-2 text-sm font-medium text-white transition-all duration-200 hover:opacity-90 disabled:opacity-40"
          >
            Use this draft
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
